import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api, { getErrorMessage } from '../../utils/api';
import { buildLocallyToggledEntity, extractEntityFromTogglePayload } from '../../utils/toggleEntityState';

const ADMIN = '/admin';

// GET /admin/get-all-services (filters: salon_id, category_id, is_active)
export const fetchServices = createAsyncThunk(
  'service/fetchServices',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`${ADMIN}/get-all-services`, { params: filters });
      return data?.data ?? data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to fetch services');
    }
  }
);

// GET /admin/get-services-by-salon/:salonId (public)
export const fetchServicesBySalon = createAsyncThunk(
  'service/fetchServicesBySalon',
  async (salonId, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`${ADMIN}/get-services-by-salon/${salonId}`);
      return data?.data ?? data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to fetch services by salon');
    }
  }
);

// GET /admin/get-service/:id
export const fetchServiceById = createAsyncThunk(
  'service/fetchServiceById',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`${ADMIN}/get-service/${id}`);
      return data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to fetch service');
    }
  }
);

// POST /admin/create-service
export const createService = createAsyncThunk(
  'service/createService',
  async (serviceData, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`${ADMIN}/create-service`, serviceData);
      return data?.data ?? data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to create service');
    }
  }
);

// PUT /admin/update-service/:id
export const updateService = createAsyncThunk(
  'service/updateService',
  async ({ id, serviceData }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`${ADMIN}/update-service/${id}`, serviceData);
      return data?.data ?? data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to update service');
    }
  }
);

// DELETE /admin/delete-service/:id
export const deleteService = createAsyncThunk(
  'service/deleteService',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`${ADMIN}/delete-service/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to delete service');
    }
  }
);

// PATCH /admin/toggle-service-active/:id
export const toggleServiceActive = createAsyncThunk(
  'service/toggleServiceActive',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.patch(`${ADMIN}/toggle-service-active/${id}`);
      return data?.data ?? data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to toggle service');
    }
  }
);

const initialState = {
  services: [],
  servicesBySalon: [],
  selectedService: null,
  loading: false,
  error: null,
};

const serviceSlice = createSlice({
  name: 'service',
  initialState,
  reducers: {
    selectService: (state, action) => {
      state.selectedService = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchServices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchServices.fulfilled, (state, action) => {
        state.loading = false;
        state.services = action.payload || [];
      })
      .addCase(fetchServices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch services';
      })
      .addCase(fetchServicesBySalon.fulfilled, (state, action) => {
        state.servicesBySalon = action.payload || [];
      })
      .addCase(fetchServiceById.fulfilled, (state, action) => {
        state.selectedService = action.payload;
      })
      .addCase(createService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createService.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) state.services.push(action.payload);
      })
      .addCase(createService.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to create service';
      })
      .addCase(updateService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateService.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.services.findIndex((s) => s.id === action.payload?.id);
        if (index !== -1 && action.payload) state.services[index] = action.payload;
        if (state.selectedService?.id === action.payload?.id) {
          state.selectedService = action.payload;
        }
      })
      .addCase(updateService.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to update service';
      })
      .addCase(deleteService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteService.fulfilled, (state, action) => {
        state.loading = false;
        state.services = state.services.filter((s) => s.id !== action.payload);
        if (state.selectedService?.id === action.payload) state.selectedService = null;
      })
      .addCase(deleteService.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to delete service';
      })
      .addCase(toggleServiceActive.fulfilled, (state, action) => {
        const updated = extractEntityFromTogglePayload(action.payload, ['service']);
        const targetId = updated?.id ?? action.meta.arg;
        const index = state.services.findIndex((s) => s.id === targetId);

        if (index !== -1) {
          state.services[index] = updated || buildLocallyToggledEntity(state.services[index]);
        }

        if (state.selectedService?.id === targetId) {
          state.selectedService = updated || buildLocallyToggledEntity(state.selectedService);
        }
      });
  },
});

export const { selectService, clearError } = serviceSlice.actions;
export default serviceSlice.reducer;

export const selectServices = (state) => state.service.services;
export const selectServicesBySalon = (state) => state.service.servicesBySalon;
export const selectSelectedService = (state) => state.service.selectedService;
export const selectServiceLoading = (state) => state.service.loading;
export const selectServiceError = (state) => state.service.error;

export function useService() {
  const dispatch = useDispatch();
  const services = useSelector(selectServices);
  const selectedService = useSelector(selectSelectedService);
  const loading = useSelector(selectServiceLoading);
  const error = useSelector(selectServiceError);

  return {
    services,
    selectedService,
    loading,
    error,
    fetchServices: useCallback((filters) => dispatch(fetchServices(filters)), [dispatch]),
    createService: useCallback((data) => dispatch(createService(data)), [dispatch]),
    updateService: useCallback(({ id, serviceData }) => dispatch(updateService({ id, serviceData })), [dispatch]),
    deleteService: useCallback((id) => dispatch(deleteService(id)), [dispatch]),
    fetchServicesBySalon: useCallback((salonId) => dispatch(fetchServicesBySalon(salonId)), [dispatch]),
    fetchServiceById: useCallback((id) => dispatch(fetchServiceById(id)), [dispatch]),
    toggleServiceActive: useCallback((id) => dispatch(toggleServiceActive(id)), [dispatch]),
  };
}
